/**
 * BusinessCapability CRUD + bulk operations
 */

import type { BusinessCapability } from '../../graphql/types';
import { createEntityCrudClient, createBulkOperationsClient } from './crudFactory';
import type { BulkOperationResult } from './crudFactory';

export interface CreateBusinessCapabilityInput {
  name: string;
  description?: string;
  level: number;
  parentCapabilityId?: string;
  criticality: string;
  strategicImportance: string;
  lifecycleStatus: string;
  investmentPriority?: string;
  businessOwnerId: string;
  owningBusinessUnitId: string;
  stakeholderIds?: string[];
  maturityLevel?: number;
  targetMaturityLevel?: number;
  currentInvestmentLevel?: string;
  targetInvestmentLevel?: string;
  planningHorizon?: string;
  notes?: string;
  assumptions?: string;
  tags?: string[];
}

export interface UpdateBusinessCapabilityInput {
  name?: string;
  description?: string;
  level?: number;
  parentCapabilityId?: string;
  criticality?: string;
  strategicImportance?: string;
  lifecycleStatus?: string;
  investmentPriority?: string;
  businessOwnerId?: string;
  owningBusinessUnitId?: string;
  stakeholderIds?: string[];
  maturityLevel?: number;
  targetMaturityLevel?: number;
  currentInvestmentLevel?: string;
  targetInvestmentLevel?: string;
  planningHorizon?: string;
  notes?: string;
  assumptions?: string;
  tags?: string[];
}

const client = createEntityCrudClient<
  BusinessCapability,
  CreateBusinessCapabilityInput,
  UpdateBusinessCapabilityInput
>('/capabilities');

export async function createCapability(
  input: CreateBusinessCapabilityInput
): Promise<BusinessCapability> {
  return client.create(input);
}

export async function updateCapability(
  capabilityId: string,
  input: UpdateBusinessCapabilityInput
): Promise<BusinessCapability> {
  return client.update(capabilityId, input);
}

export async function deleteCapability(
  capabilityId: string,
  hardDelete: boolean = false
): Promise<void> {
  return client.remove(capabilityId, hardDelete);
}

const bulkClient =
  createBulkOperationsClient<
    Pick<
      UpdateBusinessCapabilityInput,
      'criticality' | 'strategicImportance' | 'lifecycleStatus' | 'investmentPriority'
    >
  >('/capabilities');

export async function bulkDeleteCapabilities(
  ids: string[],
  hardDelete = false
): Promise<BulkOperationResult> {
  return bulkClient.bulkDelete(ids, hardDelete);
}

export async function bulkUpdateCapabilities(
  ids: string[],
  updates: Pick<
    UpdateBusinessCapabilityInput,
    'criticality' | 'strategicImportance' | 'lifecycleStatus' | 'investmentPriority'
  >
): Promise<BulkOperationResult> {
  return bulkClient.bulkUpdate(ids, updates);
}
